import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { buyerReducer, initialBuyerState } from "./buyerSlice";
import { agentReducer, initialAgentState } from "./agentSlice";
import { uiReducer, initialUiState } from "./uiSlice";
import { getSession, ensureDevAdmin, DEV_ADMIN } from "../auth/authService";
import { listBuyers } from "../api/buyersApi";
import { getAgent } from "../api/agentsApi";
import { ensureJourneyDb } from "../db";

export const StoreContext = createContext(null);

const initialState = {
  buyer: initialBuyerState,
  agent: initialAgentState,
  ui: initialUiState,
};

function rootReducer(state, action) {
  return {
    buyer: buyerReducer(state.buyer, action),
    agent: agentReducer(state.agent, action),
    ui: uiReducer(state.ui, action),
  };
}

function seedDemoData() {
  ensureDevAdmin();
  ensureJourneyDb();
}

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(rootReducer, initialState);

  useEffect(() => {
    seedDemoData();
    const session = getSession();
    if (session) {
      dispatch({ type: "SET_SESSION", payload: session });
    }
    listBuyers().then((buyers) => {
      dispatch({ type: "SET_BUYERS", payload: buyers });
      if (session?.role === "buyer" || session?.role === "admin") {
        const current = buyers.find((item) => item.id === (session.buyerId || session.userId));
        if (current) dispatch({ type: "SET_BUYER", payload: current });
      }
    });
    if (session?.role === "agent" || session?.role === "admin") {
      getAgent(session.userId || DEV_ADMIN.id).then((agent) => {
        if (agent) dispatch({ type: "SET_AGENT", payload: agent });
      });
    }
  }, []);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
}
